import React, { useRef, useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import useFadeInSection from '../hooks/useFadeInSection';
import useAppleAnimation from '../hooks/useAppleAnimation';

// Chart.js 컴포넌트 등록 (이미 TaxCalculator에서 등록했지만 안전을 위해)
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const ComparisonSection = () => {
  const fadeInRef = useAppleAnimation('apple-slide-up');
  const chartFadeInRef = useAppleAnimation('apple-slide-up');
  const cardsRef = useAppleAnimation('staggered-animation');
  const [isVisible, setIsVisible] = useState(false);
  const chartRef = useRef(null);

  // 연소득 1억원 기준으로 계산
  const annualIncome = 100000000; // 1억원
  const investmentAmount = 50000000; // 5천만원

  // 벤처투자 공제금액 계산 (5천만원 투자 시)
  const calculateVentureDeduction = (investment) => {
    // 3천만원까지 100%, 3-5천만원 70%
    if (investment <= 30000000) {
      return investment;
    } else if (investment <= 50000000) {
      return 30000000 + (investment - 30000000) * 0.7;
    } else {
      return 30000000 + 20000000 * 0.7 + (investment - 50000000) * 0.3;
    }
  };

  // 신용카드 공제금액 계산
  const calculateCreditCardDeduction = (income, usage) => {
    const threshold = income * 0.25; // 총급여의 25%
    if (usage <= threshold) return 0;
    
    const deductibleAmount = usage - threshold;
    const calculatedDeduction = deductibleAmount * 0.15; // 신용카드 15% 공제율
    
    // 소득별 공제 한도
    let maxDeduction;
    if (income <= 70000000) {
      maxDeduction = 3000000; // 300만원
    } else {
      maxDeduction = 2500000; // 250만원 (7천만원 초과)
    }
    
    return Math.min(calculatedDeduction, maxDeduction);
  };

  const ventureDeduction = calculateVentureDeduction(investmentAmount) / 10000000; // 천만원 단위 (4.4)
  const pensionDeduction = 9000000 / 10000000; // 900만원 고정, 천만원 단위 (0.9)
  const creditCardDeduction = calculateCreditCardDeduction(annualIncome, investmentAmount) / 10000000; // 천만원 단위 (0.25)

  // 비교 데이터: 5천만원 투자/사용 시 상품별 소득공제 금액
  const comparisonData = {
    labels: ['벤처투자', '연금저축/IRP', '신용카드'],
    datasets: [{
      label: '소득공제 금액',
      data: isVisible ? [ventureDeduction, pensionDeduction, creditCardDeduction] : [0, 0, 0], // 천만원 단위
      backgroundColor: [
        'rgba(54, 162, 235, 0.8)', // 벤처투자 - 파란색
        'rgba(255, 206, 86, 0.8)', // 연금저축 - 노란색
        'rgba(75, 192, 192, 0.8)'  // 신용카드 - 청록색
      ],
      borderColor: [
        'rgba(54, 162, 235, 1)',
        'rgba(255, 206, 86, 1)',
        'rgba(75, 192, 192, 1)'
      ],
      borderWidth: 2,
      borderRadius: 8,
      borderSkipped: false,
    }]
  };

  // Intersection Observer로 차트 애니메이션 트리거
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (chartRef.current) {
      observer.observe(chartRef.current);
    }

    return () => {
      if (chartRef.current) {
        observer.unobserve(chartRef.current);
      }
    };
  }, []);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y', // 수평 바 차트
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: '5천만원 투자 시 상품별 소득공제 한도 비교',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: 20
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed.x * 1000; // 천만원을 만원으로 변환
            return `${value.toLocaleString()}만원`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: '소득공제 금액',
          font: {
            size: 14
          }
        },
        ticks: {
          callback: function(value) {
            return `${value.toLocaleString()}천만원`;
          },
          stepSize: 0.5 // 0.5천만원(500만원) 단위
        },
        max: 5 // 최대 5천만원
      },
      y: {
        title: {
          display: false
        }
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    },
    // 반응형 설정
    onResize: (chart, size) => {
      if (size.width < 768) {
        // 모바일에서는 폰트 크기 조정
        chart.options.plugins.title.font.size = 14;
        chart.options.scales.x.title.font.size = 12;
        // Y-axis title은 display: false로 설정되어 있어서 폰트 설정 불필요
      }
    }
  };


  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        {/* 제목 섹션 */}
        <div id="comparison" className="text-center mb-12 scroll-mt-[400px] md:scroll-mt-20" ref={fadeInRef}>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            다른 절세 상품과 비교해보세요
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            동일한 금액을 투자했을 때, 벤처 투자가 다른 상품 대비 얼마나 월등한 절세 효과를 제공하는지 직접 확인하세요.
          </p>
        </div>

        {/* 차트 섹션 */}
        <div className="bg-gray-50 p-3 md:p-8 rounded-2xl border border-gray-200" ref={chartFadeInRef}>
          <div className="relative w-full max-w-4xl mx-auto">
            <div 
              ref={chartRef}
              className="chart-container"
              style={{ 
                height: '450px',
                maxHeight: '550px'
              }}
            >
              <Bar 
                key="comparison-chart" 
                data={comparisonData} 
                options={chartOptions}
              />
            </div>
          </div>

          {/* 추가 설명 */}
          <div ref={cardsRef} className="mt-8 grid md:grid-cols-3 gap-6 text-center">
            <div className="staggered-animation bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">벤처투자</h3>
              <p className="text-blue-600 text-2xl font-bold mb-2">4,400만원</p>
              <p className="text-gray-600 text-base">
                5천만원 투자 시<br/>
                소득공제 금액
              </p>
            </div>

            <div className="staggered-animation bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 mx-auto mb-4 bg-yellow-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">연금저축/IRP</h3>
              <p className="text-yellow-600 text-2xl font-bold mb-2">900만원</p>
              <p className="text-gray-600 text-base">
                5천만원 납입 시<br/>
                세액공제 금액
              </p>
            </div>

            <div className="staggered-animation bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 mx-auto mb-4 bg-teal-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V5zM3 8h18M7 15h2"/>
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">신용카드</h3>
              <p className="text-teal-600 text-2xl font-bold mb-2">{(creditCardDeduction * 10000000 / 10000).toLocaleString()}만원</p>
              <p className="text-gray-600 text-base">
                5천만원 사용 시<br/>
                소득공제 금액
              </p>
            </div>
          </div>

          {/* 주의사항 */}
          <div className="mt-6 text-center text-base text-gray-500">
            <p>※ 비교 기준: 연소득 1억원, 투자/사용금액 5천만원 가정</p>
            <p>※ 개인별 소득 및 공제 상황에 따라 실제 공제액은 달라질 수 있습니다</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComparisonSection;